"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  FileText,
  BarChart3,
  Users,
  Shield,
  TrendingUp,
  Calendar,
  Filter,
} from "lucide-react"
import { Link } from "react-router-dom"

const reportCategories = [
  {
    id: "gd-summary",
    title: "General Diary Summary",
    description: "Overview of all GD entries with filtering and sorting options",
    icon: FileText,
    color: "bg-blue-100 text-blue-800",
    href: "/reports/gd-summary",
  },
  {
    id: "case-analysis",
    title: "Case Type & Section Analysis",
    description: "Frequency and nature of legal actions with visual charts",
    icon: BarChart3,
    color: "bg-green-100 text-green-800",
    href: "/reports/case-analysis",
  },
  {
    id: "suspects-victims",
    title: "Suspects & Victims Report",
    description: "Track individuals involved across GDs with linking capabilities",
    icon: Users,
    color: "bg-purple-100 text-purple-800",
    href: "/reports/suspects-victims",
  },
  // {
  //   id: "document-status",
  //   title: "Document Upload Status",
  //   description: "Track uploaded files and their processing status",
  //   icon: Upload,
  //   color: "bg-orange-100 text-orange-800",
  //   href: "/reports/document-status",
  // },
  {
    id: "officer-wise",
    title: "Officer-wise GD Entry",
    description: "Track GD entries by officers with leaderboard format",
    icon: Shield,
    color: "bg-indigo-100 text-indigo-800",
    href: "/reports/officer-wise",
  },
  // {
  //   id: "pending-records",
  //   title: "Pending/Incomplete Records",
  //   description: "Identify records with missing data and direct action links",
  //   icon: AlertTriangle,
  //   color: "bg-red-100 text-red-800",
  //   href: "/reports/pending-records",
  // },
  {
    id: "incident-trends",
    title: "Incident Trends",
    description: "Understand incident patterns over time with charts",
    icon: TrendingUp,
    color: "bg-yellow-100 text-yellow-800",
    href: "/reports/incident-trends",
  },
  // {
  //   id: "location-heatmap",
  //   title: "GD Location Heatmap",
  //   description: "Visualize hotspots of disputes/crime by location",
  //   icon: MapPin,
  //   color: "bg-teal-100 text-teal-800",
  //   href: "/reports/location-heatmap",
  // },
]

export default function ReportsPage() {
  const [selectedCategory, setSelectedCategory] = useState("all")

  const filteredReports =
    selectedCategory === "all"
      ? reportCategories
      : reportCategories.filter((report) => report.id.includes(selectedCategory))

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-black">Police Reports Dashboard</h1>
            <p className="text-gray-600 mt-1">Comprehensive reporting system for police data analysis</p>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-gray-600" />
            <span className="text-sm text-gray-600">Last updated: {new Date().toLocaleDateString("en-IN")}</span>
          </div>
        </div>        

        {/* Filter Tabs */}
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 bg-gray-100">
            <TabsTrigger value="all" className="data-[state=active]:bg-white">
              All Reports
            </TabsTrigger>
            <TabsTrigger value="gd" className="data-[state=active]:bg-white">
              GD Reports
            </TabsTrigger>
            <TabsTrigger value="case" className="data-[state=active]:bg-white">
              Case Analysis
            </TabsTrigger>
            <TabsTrigger value="officer" className="data-[state=active]:bg-white">
              Officer Reports
            </TabsTrigger>
            <TabsTrigger value="trends" className="data-[state=active]:bg-white">
              Trends & Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value={selectedCategory}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredReports.map((report) => (
                <Link key={report.id} to={report.href}>
                  <Card className="border border-gray-200 hover:shadow-lg transition-all duration-200 cursor-pointer group">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className={`p-2 rounded-lg ${report.color}`}>
                          <report.icon className="h-6 w-6" />
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <Filter className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                      <CardTitle className="text-lg text-black group-hover:text-blue-600 transition-colors">
                        {report.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-4">{report.description}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-gray-300 group-hover:border-blue-300 group-hover:text-blue-600 bg-transparent"
                      >
                        View Report
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
